import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, AlertTriangle, FileText, Download } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from './AuthProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * In-browser preview for HR templates. Auth-gated. Does NOT consume a
 * download credit — the backend serves the file via /preview without logging.
 *
 *   .docx → mammoth.js converts to HTML, rendered inside the modal.
 *   .xlsx → SheetJS reads the sheet, rendered as a styled HTML table.
 *   .pdf  → native browser PDF viewer in an iframe (blob URL).
 *
 * The heavy libs (mammoth, xlsx) are lazy-loaded the first time the user
 * clicks Preview so the initial bundle stays small.
 */
export const TemplatePreviewModal = ({ template, onClose, onDownload }) => {
  const { authHeader } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, html: null, pdfUrl: null });

  useEffect(() => {
    if (!template) return undefined;
    let cancelled = false;
    let createdUrl = null;

    (async () => {
      setState({ loading: true, error: null, html: null, pdfUrl: null });
      try {
        const resp = await axios.get(`${API}/templates/${template.id}/preview`, {
          headers: authHeader(),
          responseType: 'arraybuffer',
        });
        if (cancelled) return;
        const name = (template.file_name || '').toLowerCase();
        const mime = template.mime_type || '';

        if (name.endsWith('.pdf') || mime === 'application/pdf') {
          const blob = new Blob([resp.data], { type: 'application/pdf' });
          createdUrl = URL.createObjectURL(blob);
          setState({ loading: false, error: null, html: null, pdfUrl: createdUrl });
          return;
        }
        if (name.endsWith('.docx')) {
          const mammoth = (await import('mammoth/mammoth.browser')).default;
          const result = await mammoth.convertToHtml({ arrayBuffer: resp.data });
          if (cancelled) return;
          setState({ loading: false, error: null, html: result.value || '<p>(empty document)</p>', pdfUrl: null });
          return;
        }
        if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(new Uint8Array(resp.data), { type: 'array' });
          const sheets = wb.SheetNames.map((sn) => {
            const ws = wb.Sheets[sn];
            const html = XLSX.utils.sheet_to_html(ws, { editable: false });
            return { name: sn, html };
          });
          if (cancelled) return;
          const merged = sheets
            .map((s) => `<h3 class="text-sm font-semibold text-gray-700 mt-4 mb-2">${escapeHtml(s.name)}</h3>${s.html}`)
            .join('');
          setState({ loading: false, error: null, html: merged, pdfUrl: null });
          return;
        }
        setState({ loading: false, error: 'Preview not available for this file type — use Download to view.', html: null, pdfUrl: null });
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data
          ? (typeof err.response.data === 'string' ? err.response.data : null)
          : err.message;
        setState({ loading: false, error: detail || 'Failed to load preview.', html: null, pdfUrl: null });
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [template, authHeader]);

  if (!template) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-3 py-6"
      data-testid="template-preview-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <header className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate" data-testid="preview-title">{template.name}</h2>
              <p className="text-xs text-gray-500 truncate">{template.file_name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1"
            aria-label="Close preview"
            data-testid="preview-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4 sm:p-6 bg-gray-50" data-testid="preview-body">
          {state.loading && (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
              <p className="text-sm">Loading preview…</p>
            </div>
          )}
          {state.error && (
            <div className="flex flex-col items-center justify-center text-center text-gray-700 py-12" data-testid="preview-error">
              <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
              <p className="text-sm max-w-md">{state.error}</p>
            </div>
          )}
          {state.pdfUrl && (
            <iframe
              src={state.pdfUrl}
              title="Template PDF preview"
              className="w-full h-[70vh] rounded-lg border border-gray-200 bg-white"
              data-testid="preview-pdf-frame"
            />
          )}
          {state.html && (
            <div className="mx-auto max-w-3xl">
              {/* Scoped typography for mammoth.js HTML output. Mammoth emits
                  plain <p>, <h1-6>, <ul>, <ol>, <table>, <strong>, etc. without
                  styling; this gives a consistent, document-like read. */}
              <style>{`
                .docx-preview { font-family: Georgia, "Times New Roman", serif; color: #1f2937; line-height: 1.7; font-size: 15px; }
                .docx-preview h1 { font-size: 1.875rem; font-weight: 700; color: #111827; margin: 1.5rem 0 0.75rem; line-height: 1.25; }
                .docx-preview h2 { font-size: 1.5rem; font-weight: 700; color: #111827; margin: 1.5rem 0 0.6rem; line-height: 1.3; }
                .docx-preview h3 { font-size: 1.25rem; font-weight: 600; color: #111827; margin: 1.25rem 0 0.5rem; line-height: 1.35; }
                .docx-preview h4 { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 1rem 0 0.5rem; }
                .docx-preview h5, .docx-preview h6 { font-size: 1rem; font-weight: 600; color: #1f2937; margin: 1rem 0 0.5rem; text-transform: uppercase; letter-spacing: 0.02em; }
                .docx-preview p { margin: 0.85rem 0; }
                .docx-preview p:first-child { margin-top: 0; }
                .docx-preview p:last-child { margin-bottom: 0; }
                .docx-preview ul, .docx-preview ol { margin: 0.85rem 0; padding-left: 1.75rem; }
                .docx-preview ul li, .docx-preview ol li { margin: 0.35rem 0; }
                .docx-preview ul { list-style: disc; }
                .docx-preview ol { list-style: decimal; }
                .docx-preview ul ul, .docx-preview ol ol, .docx-preview ul ol, .docx-preview ol ul { margin: 0.25rem 0; }
                .docx-preview strong, .docx-preview b { font-weight: 700; color: #111827; }
                .docx-preview em, .docx-preview i { font-style: italic; }
                .docx-preview a { color: #1d4ed8; text-decoration: underline; word-break: break-word; }
                .docx-preview a:hover { color: #1e40af; }
                .docx-preview blockquote { border-left: 4px solid #dbeafe; padding-left: 1rem; color: #4b5563; margin: 1rem 0; font-style: italic; }
                .docx-preview hr { border: 0; border-top: 1px solid #e5e7eb; margin: 1.5rem 0; }
                .docx-preview table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.92em; }
                .docx-preview table th, .docx-preview table td { border: 1px solid #e5e7eb; padding: 0.5rem 0.75rem; text-align: left; vertical-align: top; }
                .docx-preview table th { background: #f9fafb; font-weight: 600; color: #111827; }
                .docx-preview table tr:nth-child(even) td { background: #fafafa; }
                .docx-preview img { max-width: 100%; height: auto; margin: 1rem 0; border-radius: 4px; }
                .docx-preview > *:first-child { margin-top: 0; }
              `}</style>
              <div
                className="docx-preview bg-white rounded-lg border border-gray-200 px-7 sm:px-12 py-8 sm:py-10 shadow-sm"
                data-testid="preview-html"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: state.html }}
              />
            </div>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-500">
            Previews are free and don&apos;t count against your monthly limit.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="preview-cancel" className="text-sm h-9">
              Close
            </Button>
            <Button
              onClick={() => onDownload(template)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9"
              data-testid="preview-download"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
